import axios from "axios";
import moment from "moment-with-locales-es6";
import React, {
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";

import { DOCUMENT_TYPES, getDriverInfo } from "../../services/GetDriverInfo";
import { saveSessionDriverAddressConfirmation } from "../../services/ValidateDriverAddress";

import { removeAccents } from "../../utils/string-utils";
import { useOnInit } from "../../utils/useOnInit";
import { DomainContext } from "../../domain-repository/DomainRepository";
import { driverInfoReducer } from "./driverInfoReducer";
import {
  checkDriverInfoStepEditMode,
  COUNTRY_CODE_PREFIX,
  DriverFieldsErrors,
  filterCountries,
  getFormatedPhoneAndCountryCode,
  removePtDriversLicenceInvalidSpaces,
  validValidationObject,
} from "./driverInfoStepUtils";
import {
  additionalDriverFieldsPresent,
  useValidateAddress,
  useValidatePostalCode,
  validateCity,
  validateCountry,
  validateDate,
  validateDocumentID,
  validateDriversLicense,
  validateDriversLicenseExpirationDate,
  validateDriversLicenseIssuedDate,
  validateNotEmpty,
} from "./driver-fields-validations";
import {
  buildMomentFromDateString,
  isPortugalCountry,
  isPortugalCountryId,
  padZero,
} from "../../utils/utils";

import {
  AlertModal,
  AlertModalWithActions,
} from "../../components/alert-modal/AlertModal";
import DriverInfoDropdown from "./components/DriverInfoDropdown";
import PageHeader from "../../components/PageHeader";
import Footer from "../../components/Footer";
import Loading from "../../components/Loading";
import TrackingPage from "../../components/TrackingPage";
import withLang from "../../withLang";
import { DriverInfoPage } from "./components/DriverInfoPage";
import { LabeledDriverInfoInput } from "./components/DriverInfoInput";
import { Spacer } from "../../components/Spacer";
import DriverInfoOptionsInput from "./components/DriverInfoOptionsInput";
import DriverDateInput from "./components/DriverDateInput";
import { getDriversCountryIdByName } from "./DriverStep1Page";
import AditionalDriverPopup from "./AditionalDriverPopup";
import alertModalManager from '../../components/alert-modal-wraper/alert-modal-manager'

function DriverStep3Page(props) {
  const { t, i18n } = useTranslation();

  const {
    addDriverInfo,
    setDriverInfo,
    driverInfo,
    getCountries,
    driverInfoHasClickedBack,
    setDriverInfoHasClickedBack,
    driverInfoEditMode,
    setDriverInfoEditMode,
    reservation,
    flowPreviousRoute,
    visitedRoutes,
    addVisitedRoutes,
    invalidBirthdates,
    isEligibleForYoungDriver,
    getDriversMinimumAgeForReservation,
    getDriversPhoneCountryCode,
    hasAdditionalDriver,
    setInvalidAddressAlert,
    additionalDriverInfo,
    groupsDetails,
  } = useContext(DomainContext);
  const safeReservation = reservation || {};

  const vehicleGroupId = safeReservation.GroupID;
  const groupInfo = groupsDetails.find(g => g.id === vehicleGroupId) || {};

  const countries = getCountries();

  const countryPhoneCodes = countries.filter((c) => c.PhoneCountryCode);
  const formattedPhone = getFormatedPhoneAndCountryCode(
    driverInfo.phone,
    countryPhoneCodes
  );

  const isLicencePT = useMemo(
    () => isPortugalCountry(driverInfo.driversLicenseIssuedPlace),
    [driverInfo.driversLicenseIssuedPlace]
  );

  const [state, dispatch] = useReducer(driverInfoReducer, {
    canDisplayErrors: false,

    address: driverInfo.address || "",
    doorNumber: driverInfo.doorNumber || "",
    postalCode: driverInfo.postalCode || "",
    city: driverInfo.city || "",

    driversLicense: isLicencePT
      ? removePtDriversLicenceInvalidSpaces(driverInfo.driversLicense)
      : driverInfo.driversLicense,

    driversLicenseIssuedDay:
      (driverInfo.driversLicenseIssuedDate &&
        buildMomentFromDateString(driverInfo.driversLicenseIssuedDate).date()) ||
      "",
    driversLicenseIssuedMonth:
      (driverInfo.driversLicenseIssuedDate &&
        buildMomentFromDateString(driverInfo.driversLicenseIssuedDate).month() + 1) ||
      "",
    driversLicenseIssuedYear:
      (driverInfo.driversLicenseIssuedDate &&
        buildMomentFromDateString(driverInfo.driversLicenseIssuedDate).year()) ||
      "",

    driversLicenseExpirationDay:
      (driverInfo.driversLicenseExpirationDate &&
        buildMomentFromDateString(driverInfo.driversLicenseExpirationDate).date()) ||
      "",
    driversLicenseExpirationMonth:
      (driverInfo.driversLicenseExpirationDate &&
        buildMomentFromDateString(driverInfo.driversLicenseExpirationDate).month() + 1) ||
      "",
    driversLicenseExpirationYear:
      (driverInfo.driversLicenseExpirationDate &&
        buildMomentFromDateString(driverInfo.driversLicenseExpirationDate).year()) ||
      "",

    popupInfo: {
      isVisible: false,
      message: "",
      title: "",
    },
    minimumLicencePopupInfo: {
      isVisible: false,
      message: '',
      title: ''
    },
    errorModal: null,
    isLoading: null,

    add1PopupOpen: false,

    skipAddressValidation: false,
    invalidAddressAlert: null,
    invalidDoorNumberAlert: false,
  });

  const [validateCanSkip, setValidateCanSkip] = useState(
    !visitedRoutes.includes(window.location.pathname) && !driverInfoEditMode && !driverInfoHasClickedBack
  );

  const expireDateOptional = useMemo(() => {
    return (
      reservation.configurations?.driversLicenseExpirationDateCountries?.includes(
        getDriversCountryIdByName(
          countries,
          driverInfo.driversLicenseIssuedPlace
        )
      ) || false
    );
  }, [
    reservation.configurations,
    countries,
    driverInfo.driversLicenseIssuedPlace,
  ]);

  const issuedDateOptional = useMemo(() => {
    return (
      reservation.configurations?.driversLicenseEmissionDateCountries?.includes(
        getDriversCountryIdByName(countries, driverInfo.driversLicenseIssuedPlace)
      ) || false
    );
  }, [reservation.configurations, countries, driverInfo.driversLicenseIssuedPlace]);

  const { data: addressValidation, isLoading: isLoadingAddressValidation } =
    useValidateAddress(t, state.address, state.doorNumber, state.skipAddressValidation);

  const {
    data: postalCodeValidation,
    isLoading: isLoadingPostalCodeValidation,
  } = useValidatePostalCode(t, state.postalCode, driverInfo.countryID);

  const driversLicenseValidation = useMemo(
    () => validateDriversLicense(t, state.driversLicense, isLicencePT),
    [state.driversLicense, isLicencePT, t]
  );

  const cityValidation = useMemo(
    () => validateCity(t, state.city, driverInfo.countryID),
    [state.city, driverInfo.countryID, t]
  );

  const driversLicenseIssuedDateValidation = useMemo(
    () =>
      validateDriversLicenseIssuedDate(
        t,
        state.driversLicenseIssuedDay,
        state.driversLicenseIssuedMonth,
        state.driversLicenseIssuedYear,
        issuedDateOptional,
      ),
    [
      state.driversLicenseIssuedDay,
      state.driversLicenseIssuedMonth,
      state.driversLicenseIssuedYear,
      t,
      issuedDateOptional,
    ]
  );

  const driversLicenseExpirationDateValidation = useMemo(
    () =>
      validateDriversLicenseExpirationDate(
        t,
        state.driversLicenseExpirationDay,
        state.driversLicenseExpirationMonth,
        state.driversLicenseExpirationYear,
        expireDateOptional,
        state.driversLicenseIssuedDay,
        state.driversLicenseIssuedMonth,
        state.driversLicenseIssuedYear,
        issuedDateOptional,
      ),
    [
      state.driversLicenseExpirationDay,
      state.driversLicenseExpirationMonth,
      state.driversLicenseExpirationYear,
      state.driversLicenseIssuedDay,
      state.driversLicenseIssuedMonth,
      state.driversLicenseIssuedYear,
      expireDateOptional,
      issuedDateOptional,
      t,
    ]
  );

  const canNext = useCallback(() => {
    return (
      addressValidation?.isValid &&
      postalCodeValidation?.isValid &&
      cityValidation.isValid &&
      driversLicenseValidation.isValid &&
      driversLicenseIssuedDateValidation.isValid &&
      driversLicenseExpirationDateValidation.isValid
    );
  }, [
    addressValidation,
    postalCodeValidation,
    cityValidation,
    driversLicenseValidation,
    driversLicenseIssuedDateValidation,
    driversLicenseExpirationDateValidation,
  ]);

  useEffect(() => {
    const editMode = checkDriverInfoStepEditMode(props.location.search);
    setDriverInfoEditMode(editMode);
  }, [props.location.search]);

  const handleProceedNextPage = useCallback(
    (type, skipIssuedDateValidation) => {
      const issuedDateEmptyValid = issuedDateOptional && !state.driversLicenseIssuedDay && !state.driversLicenseIssuedMonth && !state.driversLicenseIssuedYear
      if (!skipIssuedDateValidation && issuedDateEmptyValid) {
        dispatch({
          type: 'SET_VALUES',
          payload: {
            minimumLicencePopupInfo: {
              isVisible: true,
              title: t('minimumYearsLicenceTitle', { minimumLicenceTime: groupInfo.minimumLicenceTime }),
              message: ''
            }
          }
        });
        return null;
      }

      addDriverInfo({
        address: state.address,
        doorNumber: state.doorNumber,
        postalCode: state.postalCode,
        city: state.city,

        driversLicense: state.driversLicense,
        driversLicenseIssuedDate: state.driversLicenseIssuedYear ? 
          `${state.driversLicenseIssuedYear}-${state.driversLicenseIssuedMonth}-${state.driversLicenseIssuedDay}` : undefined,
        driversLicenseExpirationDate: state.driversLicenseExpirationYear
          ? `${state.driversLicenseExpirationYear}-${state.driversLicenseExpirationMonth}-${state.driversLicenseExpirationDay}`
          : undefined,
      });
      setDriverInfoHasClickedBack(false);

      if (
        !safeReservation.isPastPickupHour &&
        !safeReservation.disableSales &&
        (!additionalDriverFieldsPresent(additionalDriverInfo))
      ) {
        dispatch({
          type: "SET_VALUES",
          payload: { add1PopupOpen: true },
        });
        setValidateCanSkip(false)
      } else {
        const url = "/driver-info-review";
        if (type === "redirect") {
          props.history.replace(url);
        } else {
          props.history.push(url);
        }
      }
    },
    [
      setDriverInfoHasClickedBack,
      state,
      props.history,
      t,
    ]
  );

  const onNextClick = useCallback(
    (type) => {
      if (isLoadingAddressValidation || isLoadingPostalCodeValidation) {
        return null
      }
      const canSkipPage = canNext();
      dispatch({ type: "SET_VALUES", payload: { canDisplayErrors: true } });
      if (canSkipPage) {
        handleProceedNextPage(type);
      } else {
        if (postalCodeValidation?.isValid && cityValidation.isValid && driversLicenseValidation.isValid && driversLicenseIssuedDateValidation.isValid && driversLicenseExpirationDateValidation.isValid) {
          if (addressValidation.invalidDoorNumberAlert) {
            dispatch({ type: 'SET_VALUES', payload: { invalidDoorNumberAlert: true } });
          } else if (addressValidation.invalidAddressAlert) {
            dispatch({ type: 'SET_VALUES', payload: { invalidAddressAlert: addressValidation.message } });
          }
        }
      }
    },
    [handleProceedNextPage, canNext, isLoadingAddressValidation, isLoadingPostalCodeValidation, postalCodeValidation, cityValidation, driversLicenseValidation, driversLicenseIssuedDateValidation, driversLicenseExpirationDateValidation, addressValidation]
  );

  const onBack = useCallback(() => {
    setDriverInfoHasClickedBack(true);
    props.history.goBack();
  }, [props.history, setDriverInfoHasClickedBack]);

  const onMountRef = React.useRef(false);
  useEffect(() => {
    if (
      !onMountRef.current &&
      addressValidation !== undefined &&
      postalCodeValidation !== undefined &&
      !state.hasTyped &&
      !visitedRoutes.includes(window.location.pathname) &&
      !driverInfoEditMode && !driverInfoHasClickedBack
    ) {
      onMountRef.current = true;

      const canSkipPage = canNext();
      if (canSkipPage) {
        handleProceedNextPage("redirect");
      } else {
        addVisitedRoutes(window.location.pathname);
        setValidateCanSkip(false);
      }
    }
  }, [
    addressValidation,
    postalCodeValidation,
    driverInfoEditMode,
    driverInfoHasClickedBack,
    handleProceedNextPage,
    canNext,
    state.validatingCanSkipPage,
    state.hasTyped,
  ]);

  function handleAdditionalDriverNo() {
    props.history.push("/driver-info-review");
  }

  function handleAdditionalDriverYes() {
    props.history.push({
      pathname: "/additional-driver-info-step/1",
      search: `returnTo=/driver-info-review`,
      state: { privacyReturnTo: "/driver-info-review" },
    });
  }

  const additionalDriverPresent = hasAdditionalDriver();

  async function handleConfirmAddressClick() {
    await saveSessionDriverAddressConfirmation();
    dispatch({ type: "SET_VALUES", payload: { skipAddressValidation: true, invalidDoorNumberAlert: false } });
  }

  function onMinimumLicenYearsPopupPositiveClick() {
    dispatch({ type: 'SET_VALUE', payload: { minimumLicencePopupInfo: { isVisible: false } } });
    handleProceedNextPage(null, true)
  }

  function onMinimumLicenYearsPopupNegativeClick() {
    alertModalManager.openModal({ title: t('mobileMinimumYearsLicenceAlertTitle') })
  }

  return (
    <DriverInfoPage isLoading={validateCanSkip}>
      <PageHeader
        title={t("personalInfo")}
        active={1}
        progress={"30%"}
        handleLogoClick={() => props.history.push("/start")}
      />
      <main className="page-layout">
        <div className="container grow">
          <Loading
            active={state.isLoading}
            message={t("validatingInformation")}
          />
          <div className="driver-content" style={{ height: "100%" }}>
            <form>
              <LabeledDriverInfoInput
                labelComp={
                  <span>
                    {t("address")}
                    <span className="drivers-info-inputs__label-hint">
                      ({t("addressHint")})
                    </span>
                  </span>
                }
                id="address"
                value={state.address}
                onChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { address: value },
                  })
                }
                validationObject={
                  state.canDisplayErrors
                    ? addressValidation
                    : validValidationObject
                }
              />
              <Spacer />
              <div className="flex gap-4 items-center">
                <LabeledDriverInfoInput
                  wrapperStyle={{ width: '80px' }}
                  label={t("doorNumber")}
                  id="doorNumber"
                  value={state.doorNumber}
                  onChange={(value) =>
                    dispatch({
                      type: "SET_VALUES",
                      payload: { doorNumber: value },
                    })
                  }
                  validationObject={
                    state.canDisplayErrors
                      ? { ...addressValidation, message: '' }
                      : validValidationObject
                  }
                />
                <Spacer />
                <LabeledDriverInfoInput
                  wrapperStyle={{ flex: '1' }}
                  label={t("postalCode")}
                  id="postalCode"
                  value={state.postalCode}
                  placeholder={driverInfo.countryID === "33" ? "0000-000" : ""}
                  onChange={(value) =>
                    dispatch({
                      type: "SET_VALUES",
                      payload: { postalCode: value },
                    })
                  }
                  validationObject={
                    state.canDisplayErrors
                      ? postalCodeValidation
                      : validValidationObject
                  }
                />
              </div>
              {!isPortugalCountryId(driverInfo.countryID) && (
                <>
                  <Spacer />
                  <LabeledDriverInfoInput
                    label={t("city")}
                    id="city"
                    value={state.city}
                    onChange={(value) =>
                      dispatch({
                        type: "SET_VALUES",
                        payload: { city: value },
                      })
                    }
                    validationObject={
                      state.canDisplayErrors
                        ? cityValidation
                        : validValidationObject
                    }
                  />
                </>
              )}
              <Spacer />
              <LabeledDriverInfoInput
                label={t("driversLicense")}
                id="driversLicense"
                value={state.driversLicense}
                placeholder={
                  isPortugalCountry(driverInfo.driversLicenseIssuedPlace)
                    ? "AA-1234567 8"
                    : ""
                }
                className={
                  isPortugalCountry(driverInfo.driversLicenseIssuedPlace)
                    ? "highlight-placeholder"
                    : ""
                }
                onChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { driversLicense: value },
                  })
                }
                validationObject={
                  state.canDisplayErrors
                    ? driversLicenseValidation
                    : validValidationObject
                }
              />
              <Spacer />
              <DriverDateInput
                t={t}
                label={t("driversLicenseIssuedDate")}
                labelComp={
                  issuedDateOptional ? (
                    <span>
                      {t('driversLicenseIssuedDate')} <span className="italic ml-1">{'(' + t('optional') + ')'}</span>
                    </span>
                  ) : (
                    undefined
                  )
                }
                dayValue={state.driversLicenseIssuedDay}
                dayName="driversLicenseIssuedDay"
                monthValue={state.driversLicenseIssuedMonth}
                monthName="driversLicenseIssuedMonth"
                yearValue={state.driversLicenseIssuedYear}
                yearName="driversLicenseIssuedYear"
                onDayChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { driversLicenseIssuedDay: value },
                  })
                }
                onMonthChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { driversLicenseIssuedMonth: value },
                  })
                }
                onYearChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { driversLicenseIssuedYear: value },
                  })
                }
                validationObject={
                  state.canDisplayErrors
                    ? driversLicenseIssuedDateValidation
                    : validValidationObject
                }
              />
              <Spacer />
              <DriverDateInput
                t={t}
                labelComp={
                  expireDateOptional ? (
                    <span>
                      {t('driversLicenseExpirationDate')} <span className="italic ml-1">{'(' + t('optional') + ')'}</span>
                    </span>
                  ) : (
                    undefined
                  )
                }
                label={t("driversLicenseExpirationDate")}
                dayValue={state.driversLicenseExpirationDay}
                dayName="driversLicenseExpirationDay"
                monthValue={state.driversLicenseExpirationMonth}
                monthName="driversLicenseExpirationMonth"
                yearValue={state.driversLicenseExpirationYear}
                yearName="driversLicenseExpirationYear"
                onDayChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { driversLicenseExpirationDay: value },
                  })
                }
                onMonthChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { driversLicenseExpirationMonth: value },
                  })
                }
                onYearChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { driversLicenseExpirationYear: value },
                  })
                }
                validationObject={
                  state.canDisplayErrors
                    ? driversLicenseExpirationDateValidation
                    : validValidationObject
                }
              />
            </form>
          </div>
        </div>
        <Footer
          prevHidden={driverInfoEditMode}
          handleNextClick={onNextClick}
          handlePrevClick={onBack}
        />
      </main>
      <AlertModalWithActions
        isOpen={state.invalidDoorNumberAlert}
        title={t('confirmDoorNumber')}
        message={''}
        handleLeftClick={() => dispatch({ type: 'SET_VALUES', payload: { invalidDoorNumberAlert: false } })}
        leftLabel={t('changeAddressButton')}
        handleRightClick={handleConfirmAddressClick}
        rightLabel={t('confirmAddressButton')}
      />
      <AlertModalWithActions
        isOpen={state.minimumLicencePopupInfo.isVisible}
        title={state.minimumLicencePopupInfo.title}
        message={''}
        handleLeftClick={onMinimumLicenYearsPopupNegativeClick}
        leftLabel={t('minimumLicenceRefuse')}
        handleRightClick={onMinimumLicenYearsPopupPositiveClick}
        rightLabel={t('minimumLicenceConfirm')}
      />
      <AditionalDriverPopup
        isOpen={state.add1PopupOpen}
        handleNoClick={handleAdditionalDriverNo}
        handleYesClick={handleAdditionalDriverYes}
        additionalDriverPresent={additionalDriverPresent}
      />
    </DriverInfoPage>
  );
}

export default withLang(TrackingPage(DriverStep3Page, 2006));
